import { motion } from "framer-motion";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-10">
            <Button variant="ghost" size="icon" className="border border-primary/20 hover:bg-primary/10" asChild>
              <a href="#/"><ArrowLeft className="w-5 h-5 text-primary" /></a>
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-primary" />
              </div>
              <h1
                className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-chart-1 bg-clip-text text-transparent"
                style={{ fontFamily: "Orbitron, sans-serif" }}
              >
                Política de Privacidad
              </h1>
            </div>
          </div>

          <p className="text-muted-foreground mb-10 text-sm">
            Última actualización: 11 de mayo de 2026
          </p>

          <div className="space-y-10 text-muted-foreground leading-relaxed">

            {/* Sección 1 */}
            <Section title="1. Introducción">
              <p>
                En <strong className="text-foreground">SofIA</strong> nos comprometemos a proteger y respetar su privacidad. Esta Política de Privacidad explica cómo recopilamos, usamos, almacenamos y protegemos su información personal cuando utiliza nuestros servicios de agentes de inteligencia artificial.
              </p>
              <p>
                Al usar nuestros servicios, usted acepta las prácticas descritas en esta política. Le recomendamos leerla detenidamente.
              </p>
            </Section>

            {/* Sección 2 */}
            <Section title="2. Información que Recopilamos">
              <p>Recopilamos los siguientes tipos de información:</p>
              <div className="mt-3 space-y-4">
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <h3 className="text-foreground font-semibold mb-2">📋 Información que usted nos proporciona</h3>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    <li>Nombre completo y dirección de correo electrónico</li>
                    <li>Información de facturación y pago</li>
                    <li>Nombre de empresa (si aplica)</li>
                    <li>Mensajes y comunicaciones con nuestro equipo</li>
                    <li>Preferencias y configuraciones del agente</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <h3 className="text-foreground font-semibold mb-2">🤖 Datos generados por el uso del servicio</h3>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    <li>Conversaciones e interacciones con los agentes de IA</li>
                    <li>Tareas automatizadas y flujos de trabajo configurados</li>
                    <li>Registros de uso, frecuencia y patrones de actividad</li>
                    <li>Datos de rendimiento y métricas del agente</li>
                  </ul>
                </div>
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <h3 className="text-foreground font-semibold mb-2">🌐 Datos técnicos automáticos</h3>
                  <ul className="list-disc list-inside space-y-1 pl-2">
                    <li>Dirección IP y datos de geolocalización aproximada</li>
                    <li>Tipo de navegador y sistema operativo</li>
                    <li>Páginas visitadas y tiempo de sesión</li>
                    <li>Cookies y tecnologías de rastreo similares</li>
                  </ul>
                </div>
              </div>
            </Section>

            {/* Sección 3 */}
            <Section title="3. Cómo Usamos su Información">
              <p>Utilizamos la información recopilada para los siguientes propósitos:</p>
              <ul className="list-disc list-inside space-y-2 mt-3 pl-2">
                <li>Prestar y mejorar continuamente nuestros servicios de IA.</li>
                <li>Personalizar la experiencia del agente según sus preferencias y patrones.</li>
                <li>Procesar pagos y gestionar su suscripción.</li>
                <li>Enviar notificaciones importantes sobre el servicio, actualizaciones y seguridad.</li>
                <li>Responder a sus consultas y brindar soporte técnico.</li>
                <li>Analizar el uso agregado para mejorar nuestros algoritmos y modelos de IA.</li>
                <li>Cumplir con obligaciones legales y regulatorias.</li>
                <li>Prevenir fraudes y garantizar la seguridad de la plataforma.</li>
              </ul>
            </Section>

            {/* Sección 4 */}
            <Section title="4. Compartición de Datos con Terceros">
              <p>
                <strong className="text-foreground">No vendemos su información personal a terceros.</strong> Sin embargo, podemos compartir datos en las siguientes circunstancias limitadas:
              </p>
              <ul className="list-disc list-inside space-y-2 mt-3 pl-2">
                <li><strong className="text-foreground">Proveedores de servicios:</strong> Empresas que nos ayudan a operar (procesadores de pago, hosting, analytics), bajo estrictos acuerdos de confidencialidad.</li>
                <li><strong className="text-foreground">Cumplimiento legal:</strong> Cuando sea requerido por ley, orden judicial o autoridad gubernamental competente.</li>
                <li><strong className="text-foreground">Protección de derechos:</strong> Para proteger los derechos, propiedad o seguridad de SofIA, sus usuarios o el público.</li>
                <li><strong className="text-foreground">Transferencia empresarial:</strong> En caso de fusión, adquisición o venta de activos, con notificación previa a los usuarios.</li>
              </ul>
            </Section>

            {/* Sección 5 */}
            <Section title="5. Seguridad de los Datos">
              <p>
                Implementamos medidas de seguridad técnicas y organizativas de nivel empresarial para proteger su información:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {[
                  { emoji: "🔐", title: "Encriptación AES-256", desc: "Todos los datos en reposo y en tránsito están encriptados con estándares militares." },
                  { emoji: "🛡️", title: "Autenticación 2FA", desc: "Doble factor de autenticación disponible para todas las cuentas." },
                  { emoji: "🔍", title: "Auditorías regulares", desc: "Revisiones periódicas de seguridad y pruebas de penetración." },
                  { emoji: "🏢", title: "Centros de datos seguros", desc: "Infraestructura en servidores certificados ISO 27001 y SOC 2." },
                ].map((item) => (
                  <div key={item.title} className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                    <p className="text-lg mb-1">{item.emoji} <strong className="text-foreground">{item.title}</strong></p>
                    <p className="text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Sección 6 */}
            <Section title="6. Cookies y Tecnologías de Rastreo">
              <p>
                Utilizamos cookies y tecnologías similares para mejorar su experiencia. Puede controlar el uso de cookies desde la configuración de su navegador, aunque esto puede afectar algunas funcionalidades del servicio.
              </p>
              <ul className="list-disc list-inside space-y-2 mt-3 pl-2">
                <li><strong className="text-foreground">Cookies esenciales:</strong> Necesarias para el funcionamiento básico del servicio.</li>
                <li><strong className="text-foreground">Cookies de rendimiento:</strong> Para analizar cómo se usa el servicio y mejorarlo.</li>
                <li><strong className="text-foreground">Cookies de preferencias:</strong> Para recordar sus configuraciones y personalización.</li>
              </ul>
            </Section>

            {/* Sección 7 */}
            <Section title="7. Sus Derechos">
              <p>Usted tiene los siguientes derechos sobre sus datos personales:</p>
              <div className="mt-3 space-y-3">
                {[
                  { right: "Acceso", desc: "Solicitar una copia de toda la información que tenemos sobre usted." },
                  { right: "Rectificación", desc: "Corregir datos inexactos o desactualizados." },
                  { right: "Eliminación", desc: "Solicitar la eliminación de sus datos ('derecho al olvido')." },
                  { right: "Portabilidad", desc: "Recibir sus datos en formato estructurado y legible por máquina." },
                  { right: "Oposición", desc: "Oponerse al procesamiento de sus datos para ciertos fines." },
                  { right: "Restricción", desc: "Solicitar que limitemos el uso de sus datos en ciertas circunstancias." },
                ].map((item) => (
                  <div key={item.right} className="flex gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10">
                    <span className="text-primary font-semibold min-w-[120px]">✦ {item.right}</span>
                    <span>{item.desc}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                Para ejercer cualquiera de estos derechos, contáctenos en <strong className="text-foreground">privacy@softservicesia.com</strong>. Responderemos en un plazo máximo de 30 días.
              </p>
            </Section>

            {/* Sección 8 */}
            <Section title="8. Retención de Datos">
              <p>
                Conservamos sus datos personales durante el tiempo que su cuenta esté activa y por un período adicional de <strong className="text-foreground">3 años</strong> después de la cancelación, salvo que usted solicite su eliminación o la ley requiera un período diferente.
              </p>
              <p>
                Los datos de conversaciones con los agentes de IA se retienen por <strong className="text-foreground">12 meses</strong> para permitir la mejora continua del servicio, después de lo cual son eliminados o anonimizados de forma permanente.
              </p>
            </Section>

            {/* Sección 9 */}
            <Section title="9. Menores de Edad">
              <p>
                Nuestros servicios no están dirigidos a personas menores de 18 años. No recopilamos intencionalmente información de menores. Si descubrimos que hemos recopilado datos de un menor, los eliminaremos de inmediato.
              </p>
            </Section>

            {/* Sección 10 */}
            <Section title="10. Cambios a esta Política">
              <p>
                Podemos actualizar esta Política de Privacidad periódicamente. Le notificaremos sobre cambios significativos por correo electrónico o mediante un aviso prominente en nuestro sitio web, al menos 15 días antes de que entren en vigor.
              </p>
            </Section>

            {/* Sección 11 */}
            <Section title="11. Contacto y DPO">
              <p>
                Si tiene preguntas, inquietudes o desea ejercer sus derechos de privacidad, contáctenos:
              </p>
              <div className="mt-3 p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-1">
                <p><strong className="text-foreground">SofIA — Oficial de Protección de Datos</strong></p>
                <p>Email: privacy@softservicesia.com</p>
                <p>Email legal: legal@softservicesia.com</p>
                <p>Sitio web: www.softservicesia.com</p>
              </div>
            </Section>

          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4 }}
      className="border-l-2 border-primary/30 pl-6"
    >
      <h2
        className="text-xl font-semibold text-foreground mb-4"
        style={{ fontFamily: "Orbitron, sans-serif" }}
      >
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </motion.div>
  );
}
